function html(strings: TemplateStringsArray, ...values: unknown[]): string {
  return strings.reduce((result, str, i) => result + str + String(values[i] ?? ''), '');
}

export async function buildChatDrawerTemplate(uiConfig?: UIConfig) {
  return uiConfig ? `${Chat(uiConfig)}` : '';
}

function Chat(uiConfig: UIConfig) {
  return html`
    ${!uiConfig.config?.chat_drawer?.enable && uiConfig.config?.chat_button?.lp_div_id
      ? html`<div id="${uiConfig.config.chat_button.lp_div_id}"></div>`
      : ''}
    ${FloatingChatButton(uiConfig)} ${ChatDrawer(uiConfig)}
  `;
}

function FloatingChatButton(uiConfig: UIConfig) {
  const { config, translation } = uiConfig;

  return config.chat_button?.solo_image
    ? html`
        <div
          class="live-chat-button live-chat-button--solo-img js-live-chat-button"
          role="button"
          tabindex="0"
        >
          <span
            class="live-chat-button__icon--loading js-live-chat-button-loading live-chat-spinner live-chat-hidden"
          ></span>
          <img class="live-chat-button__icon" />
        </div>
      `
    : html`
        <div
          class="live-chat-button live-chat-button--btn js-live-chat-button"
          role="button"
          tabindex="0"
        >
          ${translation.chat_button_text
            ? html` <div class="live-chat-button__copy">${translation.chat_button_text}</div> `
            : ''}
          <div class="live-chat-button__icon lc-icon"></div>
          <span
            class="live-chat-button__icon--loading js-live-chat-button-loading live-chat-spinner live-chat-hidden"
          ></span>
        </div>
      `;
}

function ChatDrawer(uiConfig: UIConfig) {
  const { config, translation } = uiConfig;

  return html`
    <div class="live-chat-drawer js-live-chat-drawer live-chat-hidden">
      ${ChatHeader(translation)}
      <div class="live-chat-drawer__content js-live-chat-drawer-content">
        ${ChatSuccess(config, translation)} ${ChatError(translation)}
      </div>
    </div>
  `;
}

function ChatHeader(translation: Translation) {
  return html`
    <div class="live-chat-drawer__header">
      <div class="live-chat-drawer__header-icon js-live-chat-header-icon lc-icon"></div>
      ${translation.header_title
        ? html` <div class="live-chat-drawer__header-text">${translation.header_title}</div> `
        : ''}
      <div
        class="live-chat-drawer__header-icon-close js-live-chat-drawer-close lc-icon"
        tabindex="0"
      ></div>
    </div>
  `;
}

function ChatSuccess(config: Config, translation: Translation) {
  return html`
    <div class="live-chat-drawer__success js-live-chat-drawer-success live-chat-hidden">
      ${ChatSuccessContent(translation)} ${ChatSuccessOptions(config, translation)}
    </div>
  `;
}

function ChatSuccessContent(translation: Translation) {
  return html`
    ${translation.success_title
      ? html`
          <div class="live-chat-drawer__title live-chat-drawer__title--success">
            ${translation.success_title}
          </div>
        `
      : ''}
    ${translation.success_description
      ? html`
          <div class="live-chat-drawer__description live-chat-drawer__description--success">
            ${translation.success_description}
          </div>
        `
      : ''}
  `;
}

function ChatSuccessOptions(config: Config, translation: Translation) {
  return html`
    ${config.chat_drawer_content?.enable_beauty_button
      ? html`
          <button class="live-chat-drawer__button js-live-chat-drawer-artist-button">
            <span
              class="live-chat-drawer__button--loading js-artist-loading live-chat-spinner live-chat-hidden"
            ></span>
            <span class="js-artist-text">${translation.beauty_button_text}</span>
            <span class="js-artist-loading-text live-chat-hidden">
              ${translation.beauty_button_loading_text}
            </span>
          </button>
        `
      : ''}
    ${config.chat_drawer_content?.enable_support_button
      ? html`
          <button class="live-chat-drawer__button js-live-chat-drawer-support-button">
            <span
              class="live-chat-drawer__button--loading js-support-loading live-chat-spinner live-chat-hidden"
            ></span>
            <span class="js-support-text">${translation.support_button_text}</span>
            <span class="js-support-loading-text live-chat-hidden">
              ${translation.support_button_loading_text}
            </span>
          </button>
        `
      : ''}
  `;
}

function ChatError(translation: Translation): string {
  return html`
    <div class="live-chat-drawer__warning js-live-chat-drawer-warning live-chat-hidden">
      <div class="live-chat-drawer__warning-icon lc-icon"></div>
      ${ChatErrorContent(translation)}
      <button class="live-chat-drawer__button js-live-chat-drawer-refresh-window-button">
        ${translation.lp_error_button_text}
      </button>
    </div>
    ${ChatErrorCookies(translation)}
  `;
}

function ChatErrorContent(translation: Translation) {
  return html`
    ${translation.lp_error_title
      ? html`
          <div class="live-chat-drawer__title live-chat-drawer__title--error">
            ${translation.lp_error_title}
          </div>
        `
      : ''}
    ${translation.lp_error_description
      ? html`
          <div class="live-chat-drawer__description live-chat-drawer__description--error">
            ${translation.lp_error_description}
          </div>
        `
      : ''}
  `;
}

function ChatErrorCookies(translation: Translation) {
  return html`
    <div class="live-chat-drawer__warning js-live-chat-drawer-cookie-warning live-chat-hidden">
      <div class="live-chat-drawer__warning-icon lc-icon"></div>
      ${ChatErrorCookiesContent(translation)} ${ChatErrorCookiesOptions(translation)}
    </div>
  `;
}

function ChatErrorCookiesContent(translation: Translation) {
  return html`
    ${translation.cookie_error_title
      ? html`
          <div class="live-chat-drawer__title live-chat-drawer__title--cookie">
            ${translation.cookie_error_title}
          </div>
        `
      : ''}
    ${translation.cookie_error_description
      ? html`
          <div class="live-chat-drawer__description live-chat-drawer__description--cookie">
            ${translation.cookie_error_description}
          </div>
        `
      : ''}
  `;
}

function ChatErrorCookiesOptions(translation: Translation) {
  return html`
    <button class="live-chat-drawer__button js-live-chat-drawer-cookie-button">
      <span class="js-live-chat-drawer-cookie-button-text">
        ${translation.cookie_error_button_text}
      </span>
      <span class="js-live-chat-drawer-cookie-button-loading-text live-chat-hidden">
        ${translation.cookie_error_button_loading_text}
      </span>
    </button>
  `;
}
